import React, {
  useState,
  useEffect,
  useRef,
  FC,
  JSX,
  ChangeEvent,
  FormEvent,
} from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import authService from "../../services/auth.service";
import { getRedirectPath } from "../../utils/authUtils";
import Button from "../../components/ui/Button";
import brandLogo from "../../assets/dhoondh2-logo.svg";
import { LocalStorageUser } from "../../types/localStorage.d";
import Toast from "../../components/ui/Toast";

interface LoginProps {
  // No props defined in the original component, but keeping the interface for consistency
}
interface Errors {
  email?: string;
  password?: string;
  remember?: string;
  submit?: string;
  otp?: string;
  mobileNumber?: string;
}

const Login: FC<LoginProps> = (): JSX.Element => {
  const [formData, setFormData] = useState<{
    mobileNumber: string;
    password: string;
  }>({
    mobileNumber: "",
    password: "",
  });

  const [errors, setErrors] = useState<Errors>({});
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [remember, setRemember] = useState<boolean>(false);
  const [useOtp, setUseOtp] = useState<boolean>(false);
  const [otp, setOtp] = useState<string>("");
  const [otpSent, setOtpSent] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' | 'info' } | null>(null);
  const heroRef = useRef<HTMLDivElement>(null);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Animation on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (heroRef.current) {
      observer.observe(heroRef.current);
    }

    return () => {
      if (heroRef.current) {
        observer.unobserve(heroRef.current);
      }
    };
  }, []);

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Errors = {};

    if (!formData.mobileNumber) {
      newErrors.mobileNumber = "Mobile number is required";
    } else if (
      !/^\d{10}$/.test(String(formData.mobileNumber).replace(/\D/g, ""))
    ) {
      newErrors.mobileNumber = "Enter a valid 10-digit mobile number";
    }

    if (!useOtp) {
      if (!formData.password) {
        newErrors.password = "Password is required";
      }
    } else {
      if (!otpSent) {
        newErrors.otp = "Send OTP first";
      } else if (!otp) {
        newErrors.otp = "Enter the OTP";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      let result;
      if (!useOtp) {
        result = await login({
          mobileNumber: formData.mobileNumber,
          password: formData.password,
          otp: "",
        });
      } else {
        // OTP-based login via /api/User/login using otp in payload
        result = await login({
          mobileNumber: formData.mobileNumber,
          password: "",
          otp,
        });
      }
      const role = result?.user?.role;

      // Show success toast and navigate after a short delay
      const performNavigate = (path: string) => {
        setToast({ message: "Signed in successfully", type: "success" });
        setTimeout(() => navigate(path, { replace: true }), 1000);
      };

      // Check if there's a return URL from the application flow
      const from = location.state?.from;
      if (from) {
        performNavigate(from);
        return;
      }

      // Get the appropriate redirect path based on user role and status
      const redirectPath = getRedirectPath(
        role as LocalStorageUser["role"] | null
      );
      performNavigate(redirectPath);
    } catch (error: any) {
      console.log("[test] error", error);
      setErrors({ submit: error.message || "Login failed" });
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    setErrors({});
    if (!/^\d{10}$/.test(String(formData.mobileNumber).replace(/\D/g, ""))) {
      setErrors({ mobileNumber: "Enter a valid 10-digit mobile number" });
      return;
    }
    setLoading(true);
    try {
      //TODO use authContext
      await authService.sendOTP({
        mobileNumber: formData.mobileNumber,
        type: "Mobile",
        requestType: "Login"
      });
      setOtpSent(true);
    } catch (error: any) {
      console.log("[test], ",error)
      if(typeof error?.data == 'string' && error.data.includes("No user found")) {
        setErrors({ submit: "No user registered with this mobile number" });
      } else {
        setErrors({ submit: error?.data || "Failed to send OTP" });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-teaGreen/5 via-white to-primary-lightGreen/10">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          position="top-right"
          duration={2500}
          onClose={() => setToast(null)}
          show
        />
      )}
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative pt-20 pb-16 md:pt-24 md:pb-20 overflow-hidden"
      >
        {/* Background Decorations */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-teaGreen/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary-lightGreen/20 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary-teaGreen/10 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`text-center transition-all duration-1000 ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-8"
            }`}
          >
            <div className="mx-auto flex items-center justify-center mb-8">
              <img
                src={brandLogo}
                alt="Dhoondh"
                className="h-12 md:h-16 aspect-auto"
              />
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-primary-black mb-6 leading-tight">
              Welcome
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-teaGreen to-primary-lightGreen">
                {" "}
                Back
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto mb-8 leading-relaxed">
              Sign in to continue your journey with Dhoondh
            </p>
          </div>
        </div>
      </section>

      {/* Login Form Section */}
      <section className="relative pb-20 md:pb-32">
        <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 md:p-10 shadow-xl border border-gray-100">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-primary-black mb-2">
                Sign in to your account
              </h2>
              <p className="text-gray-600">
                {location.state?.message ||
                  "Enter your credentials to access your account"}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-5">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Mobile Number *
                  </label>
                  <input
                    type="tel"
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-primary-teaGreen/20 ${
                      errors.mobileNumber
                        ? "border-red-300 focus:border-red-500"
                        : "border-gray-200 focus:border-primary-teaGreen"
                    }`}
                    placeholder="Enter your 10-digit mobile number"
                    required
                  />
                  <div className="flex items-center gap-3 text-sm">
                    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        className="rounded"
                        checked={useOtp}
                        onChange={(e) => setUseOtp(e.target.checked)}
                      />
                      <span className="text-gray-700">Login with OTP</span>
                    </label>
                    {useOtp && (
                      <button
                        type="button"
                        onClick={handleSendOtp}
                        disabled={loading}
                        className="text-primary-teaGreen hover:text-primary-lightGreen disabled:opacity-50"
                      >
                        {otpSent ? "Resend OTP" : "Send OTP"}
                      </button>
                    )}
                  </div>
                  {errors.mobileNumber && (
                    <p className="text-sm text-red-600 flex items-center gap-1">
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {errors.mobileNumber}
                    </p>
                  )}
                </div>

                {!useOtp ? (
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Password *
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className={`w-full pr-12 px-4 py-3 rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-primary-teaGreen/20 ${
                          errors.password
                            ? "border-red-300 focus:border-red-500"
                            : "border-gray-200 focus:border-primary-teaGreen"
                        }`}
                        placeholder="Enter your password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((p) => !p)}
                        className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                      >
                        {showPassword ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-2.218-2.218A9.67 9.67 0 0018 10c-1.55-3.6-5-6-8-6-1.302 0-2.54.34-3.657.938L3.707 2.293z" />
                            <path d="M12.454 13.868L8.132 9.546A3 3 0 0012.454 13.868z" />
                          </svg>
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path d="M10 4c-3 0-6.45 2.4-8 6 1.55 3.6 5 6 8 6s6.45-2.4 8-6c-1.55-3.6-5-6-8-6zm0 10a4 4 0 110-8 4 4 0 010 8z" />
                          </svg>
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {errors.password}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Enter OTP *
                    </label>
                    <input
                      type="text"
                      name="otp"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-primary-teaGreen/20 ${
                        errors.otp
                          ? "border-red-300 focus:border-red-500"
                          : "border-gray-2 00 focus:border-primary-teaGreen"
                      }`}
                      placeholder="Enter the OTP"
                      inputMode="numeric"
                    />
                    {errors.otp && (
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {errors.otp}
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className={`h-4 w-4 focus:ring-primary-teaGreen/20 border-gray-300 rounded ${
                      errors.remember
                        ? "ring-2 ring-red-300"
                        : "text-primary-teaGreen"
                    }`}
                  />
                  <label
                    htmlFor="remember-me"
                    className="ml-2 block text-sm text-gray-700"
                  >
                    Remember me
                  </label>
                </div>

                <div className="text-sm">
                  <Link
                    to="/forgot-password"
                    className="font-medium text-primary-lightGreen"
                  >
                    Forgot password?
                  </Link>
                </div>
              </div>

              {errors.remember && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                  <p className="text-sm text-red-700">{errors.remember}</p>
                </div>
              )}

              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <div className="flex items-center gap-2">
                    <svg
                      className="w-5 h-5 text-red-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <p className="text-sm text-red-700">{errors.submit}</p>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                size="lg"
                className="w-full py-4 text-lg font-semibold"
                loading={loading}
                disabled={loading}
              >
                {loading
                  ? useOtp
                    ? "Verifying..."
                    : "Signing in..."
                  : useOtp
                  ? "Verify & Sign In"
                  : "Sign In"}
              </Button>

              <div className="text-center space-y-3">
                <p className="text-sm text-gray-600">
                  Don't have an account?{" "}
                  <Link
                    to="/register"
                    className="font-semibold text-primary-lightGreen"
                  >
                    Create one here
                  </Link>
                </p>

                </div>
            </form>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Login;
